import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search, Stethoscope } from "lucide-react";
import { useSpecialties } from "@/api/hooks/specialties.hooks";
import { Card, CardContent } from "@/components/ui/Card";
import { Combobox } from "@/components/ui/Combobox";
import { ZipInput } from "@/components/ui/ZipInput";
import Button from "@/components/ui/Button";
import { STORAGE_KEYS } from "@/constants/storage";

export function HomePage() {
  const { data: specialties = [] } = useSpecialties();
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [zipCode, setZipCode] = useState("");
  const navigate = useNavigate();

  const options = specialties.map((s) => ({
    value: s.slug,
    label: s.name,
  }));
  const canSearch = !!selectedSpecialty && /^\d{5}$/.test(zipCode.trim());

  return (
    <div className="min-h-screen bg-background">
      <section className="relative overflow-hidden py-16 md:py-24">
        <div
          className="pointer-events-none absolute inset-0 -z-10 opacity-20"
          aria-hidden
        >
          <div className="absolute left-1/2 top-[-10%] h-[600px] w-[600px] -translate-x-1/2 rounded-full blur-3xl bg-gradient-secondary" />
        </div>

        <div className="container">
          <div className="mx-auto mb-12 max-w-4xl text-center">
            <h1 className="headline mb-6 text-4xl leading-tight text-foreground md:text-6xl">
              Priority access to{" "}
              <span className="gradient-primary bg-clip-text text-transparent">
                specialist care
              </span>
            </h1>

            <p className="subheading mb-4 text-lg text-muted-foreground md:text-xl">
              TRANSPARENT PRICE & NO INSURANCE RESTRICTIONS
            </p>

            <p className="body-copy mx-auto mb-8 max-w-3xl text-lg text-muted-foreground">
              Book with Apptly Health for urgent specialty care, fast second opinions,
              or anytime you need to bypass the delays and high costs of your
              insurance plan.
            </p>
          </div>

          <div className="mx-auto mb-12 max-w-2xl">
            <Card className="shadow-lg">
              <CardContent className="p-6">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (canSearch) {
                      const trimmedZip = zipCode.trim();
                      sessionStorage.setItem(
                        STORAGE_KEYS.SEARCHED_ZIP_CODE,
                        trimmedZip,
                      );
                      navigate({
                        to: '/search',
                        search: {
                          specialty: selectedSpecialty,
                          zipCode: trimmedZip,
                        },
                      });
                    }
                  }}
                  autoComplete="off"
                  aria-label="Specialist search"
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                      <div className="[&_input]:pl-10">
                        <Combobox
                          value={selectedSpecialty}
                          onValueChange={setSelectedSpecialty}
                          options={options}
                          placeholder="Search Specialty"
                        />
                      </div>
                    </div>
                    <ZipInput
                      value={zipCode}
                      setValue={setZipCode}
                      inputId="zip"
                      labelId="zip-label"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!canSearch}
                    className="w-full h-10 bg-gradient-primary hover:opacity-90 transition-opacity disabled:opacity-50 text-white font-semibold rounded-md"
                  >
                    Find Specialists
                  </button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-6 text-center">
              <p className="mb-2 text-base text-muted-foreground">
                Not sure which specialist you need?
              </p>
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
              >
                <Stethoscope className="mr-2 h-4 w-4" aria-hidden />
                Try our AI Symptom Checker
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
